import {
  type UseQueryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { AuthService, type UserDto } from '@/services/api';

export const authKeys = {
  me: () => ['me'] as const,
  googleAuth: () => ['googleAuth'] as const,
  appleAuth: () => ['appleAuth'] as const,
  outlookAuth: () => ['outlookAuth'] as const,
};

export const useGetAccountQuery = (
  options?: Partial<UseQueryOptions<UserDto, AxiosError>>
) => {
  return useQuery<UserDto, AxiosError>({
    queryKey: authKeys.me(),
    queryFn: () => AuthService.getAuthMe(options?.axiosRequestConfig),
    ...options,
  });
};

export const useClearAccountQuery = () => {
  const queryClient = useQueryClient();

  return () => {
    // Remove a specific query from the cache
    queryClient.removeQueries({ queryKey: authKeys.me(), exact: true });
  };
};

type GoogleAuthExternalParams = { redirectUrl?: string };

export const useGetGoogleAuthExternalQuery = (
  params: GoogleAuthExternalParams,
  options?: Partial<UseQueryOptions<string, AxiosError>>
) => {
  return useQuery<string, AxiosError>({
    queryKey: authKeys.me(),
    queryFn: () => AuthService.getAuthExternalGoogle(params),
    ...options,
  });
};

type AppleAuthExternalParams = { redirectUrl?: string };

export const useGetAppleAuthExternalQuery = (
  params: AppleAuthExternalParams,
  options?: Partial<UseQueryOptions<string, AxiosError>>
) => {
  return useQuery<string, AxiosError>({
    queryKey: authKeys.appleAuth(),
    queryFn: () => AuthService.getAuthExternalGoogle(params),
    ...options,
  });
};

type OutlookAuthExternalParams = { redirectUrl?: string };

export const useGetOutlookAuthExternalQuery = (
  params: OutlookAuthExternalParams,
  options?: Partial<
    UseQueryOptions<{ authUrl: string; codeVerifier: string }, AxiosError>
  >
) => {
  return useQuery<{ authUrl: string; codeVerifier: string }, AxiosError>({
    queryKey: authKeys.outlookAuth(),
    queryFn: () => AuthService.getAuthExternalOutlook(params),
    ...options,
  });
};
